import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Container,
  Avatar,
  alpha,
  useTheme,
  Button,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";

import LoginForm from "../forms/LoginForm";
import SetupInstitutionForm from "../forms/SetupInstitutionForm";
import AppModal from "../components/common/AppModal";
import AppCircularProgress from "../components/common/AppCircularProgress";
import PageError from "../components/common/PageError";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { checkSystemStatus } from "../redux/actions/appStatusActions";
import { AppStartupState } from "../shared/enums/AppStartupState";

const AuthPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const { startupState, startupMessage, errorSetupInstitution } =
    useAppSelector((state) => state.appStatus);

  const [isSetupModalOpen, setIsSetupModalOpen] = useState<boolean>(false);

  // Open the setup wizard if system has not been initialized
  useEffect(() => {
    if (startupState === AppStartupState.NEEDS_SETUP) {
      setIsSetupModalOpen(true);
    }
  }, [startupState]);

  const renderContent = (): React.ReactNode => {
    switch (startupState) {
      case AppStartupState.LOADING:
        return (
          <Box
            sx={{
              py: 6,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <AppCircularProgress />
            <Typography
              variant="body2"
              sx={{ mt: 3, fontWeight: 600, color: "text.secondary" }}
            >
              {startupMessage}
            </Typography>
          </Box>
        );

      case AppStartupState.ERROR:
        return (
          <Box sx={{ py: 2 }}>
            <PageError
              error={startupMessage}
              onRetry={() => dispatch(checkSystemStatus())}
            />
          </Box>
        );

      case AppStartupState.NEEDS_SETUP:
        return (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body1" sx={{ color: "text.secondary", mb: 3 }}>
              This system instance has not been configured yet. Please complete
              the initial master institution setup to generate the root
              administrator account.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AppRegistrationIcon />}
              onClick={() => setIsSetupModalOpen(true)}
            >
              Start Institution Setup
            </Button>
          </Box>
        );

      case AppStartupState.READY:
      default:
        return <LoginForm />;
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      <Container
        maxWidth="sm"
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Paper
          elevation={24}
          sx={{
            width: "100%",
            maxWidth: 450,
            p: { xs: 3, sm: 5 },
            borderRadius: theme.appRadius?.xl || 4,
            bgcolor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.background.paper, 0.8)
                : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            textAlign: "center",
          }}
        >
          {/* LOGO */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: theme.palette.secondary.main,
                boxShadow: theme.shadows[4],
              }}
            >
              <SchoolIcon fontSize="large" sx={{ color: "#fff" }} />
            </Avatar>
          </Box>

          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {startupState === AppStartupState.NEEDS_SETUP
              ? "Setup Required"
              : "Welcome Back"}
          </Typography>

          <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
            {startupState === AppStartupState.NEEDS_SETUP
              ? "Complete the initial institution deployment"
              : "Sign in to access your school workspace"}
          </Typography>

          {renderContent()}
        </Paper>
      </Container>

      {/* INSTITUTION SETUP WIZARD MODAL */}
      <AppModal
        open={isSetupModalOpen}
        onClose={() => setIsSetupModalOpen(false)}
        title="Institution Deployment & Master Setup"
        width="md"
      >
        <SetupInstitutionForm
          loading={false}
          onSuccess={() => setIsSetupModalOpen(false)}
          error={errorSetupInstitution}
        />
      </AppModal>
    </Box>
  );
};

export default AuthPage;
